import { decodeEventLog, encodeEventTopics, type Address, type Hex } from "viem";
import { ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import { currentChainId, type Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadMethod } from "./read-rpc";

const names = ["MetadataUpdate", "BatchMetadataUpdate", "RandomnessReady", "RewardFinalized", "RandomnessSamplingContinued", "Transfer"] as const;
const topics = names.map((eventName) => encodeEventTopics({ abi: ponsRewardNftAbi, eventName })[0]);
type Head = { number: bigint; hash: string };
function head(raw: unknown): Head {
  if (!raw || typeof raw !== "object" || !("number" in raw) || !("hash" in raw) ||
    typeof raw.number !== "string" || !/^0x[0-9a-f]+$/i.test(raw.number) ||
    typeof raw.hash !== "string" || !/^0x[0-9a-f]{64}$/i.test(raw.hash)) throw new Error("Invalid block head");
  return { number: BigInt(raw.number), hash: raw.hash.toLowerCase() };
}

/** Observe at most 32 recent blocks. Logs invalidate API reads; they never rewrite NFT data. */
export function watchNftEvents(provider: Eip1193Provider, input: { chainId: number; collection: Address; tokenId: bigint },
  invalidate: () => void, availability: (available: boolean) => void) {
  let stopped = false;
  let pending = false;
  let previous: Head | null = null;
  let fingerprint = "";
  const abort = new AbortController();
  const request = (method: RpcReadMethod, params: readonly unknown[]) =>
    readRpc(provider, { method, params }, { signal: abort.signal });
  const poll = async () => {
    if (stopped || pending) return;
    pending = true;
    try {
      if (await currentChainId(provider, { signal: abort.signal }) !== input.chainId) throw new Error("Wrong observation chain");
      const current = head(await request("eth_getBlockByNumber", ["latest", false]));
      let reorg = previous !== null && current.number < previous.number;
      if (previous && !reorg) {
        const canonical = head(await request("eth_getBlockByNumber", [`0x${previous.number.toString(16)}`, false]));
        reorg = canonical.hash !== previous.hash;
      }
      const from = current.number > 31n ? current.number - 31n : 0n;
      const raw = await request("eth_getLogs", [{ address: input.collection,
        fromBlock: `0x${from.toString(16)}`, toBlock: `0x${current.number.toString(16)}`, topics: [topics] }]);
      if (!Array.isArray(raw)) throw new Error("Invalid log response");
      const relevant: string[] = [];
      let removed = false;
      for (const log of raw as Record<string, unknown>[]) {
        if (!log || typeof log.address !== "string" || log.address.toLowerCase() !== input.collection.toLowerCase() ||
          typeof log.data !== "string" || !Array.isArray(log.topics)) continue;
        try {
          const event = decodeEventLog({ abi: ponsRewardNftAbi, data: log.data as Hex, topics: log.topics as [Hex, ...Hex[]] });
          if (event.eventName === "BatchMetadataUpdate") {
            if (input.tokenId < event.args._fromTokenId || input.tokenId > event.args._toTokenId) continue;
          } else {
            const id = event.eventName === "MetadataUpdate" ? event.args._tokenId :
              "tokenId" in event.args ? event.args.tokenId : null;
            if (id !== input.tokenId) continue;
          }
          removed ||= log.removed === true;
          relevant.push(JSON.stringify([log.blockHash, log.transactionHash, log.logIndex, log.removed, log.data, log.topics]));
        } catch { /* Unrelated logs are not NFT observations. */ }
      }
      if (await currentChainId(provider, { signal: abort.signal }) !== input.chainId) throw new Error("Observation chain changed");
      if (stopped) return;
      const next = relevant.sort().join("|");
      if (reorg || removed || next !== fingerprint) invalidate();
      fingerprint = next;
      previous = current;
      availability(true);
    } catch {
      if (!stopped) availability(false);
    } finally { pending = false; }
  };
  const tick = () => { poll().catch(() => { if (!stopped) availability(false); }); };
  tick();
  const timer = setInterval(tick, 12_000);
  return () => { stopped = true; abort.abort(); clearInterval(timer); };
}
